import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Chip,
    Alert,
    Snackbar,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import facilityService from '../services/facilityService';
import clientService from '../services/clientService';
import { FacilityDTO, CreateFacilityRequest, CareType } from '../types/facility.types';
import { ClientDTO } from '../types/client.types';

const FacilitySettingsPage: React.FC = () => {
    const [facilities, setFacilities] = useState<FacilityDTO[]>([]);
    const [clients, setClients] = useState<ClientDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedFacility, setSelectedFacility] = useState<FacilityDTO | null>(null);

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [facilityToDelete, setFacilityToDelete] = useState<FacilityDTO | null>(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // Form state
    const [formData, setFormData] = useState<CreateFacilityRequest>({
        name: '',
        code: '',
        clientId: '',
        admissionType: CareType.SNF,
        email: '',
        phone: '',
        fax: '',
        address: '',
        city: '',
        state: '',
        country: 'USA',
        postalCode: '',
        subdomain: '',
        licenseNumber: '',
        npiNumber: '',
        medicareProviderNumber: '',
        medicaidProviderNumber: '',
        capacity: undefined,
        timezone: 'America/New_York',
        logoUrl: '',
        website: '',
        notes: '',
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchFacilities();
        fetchClients();
    }, [page, rowsPerPage]);

    const fetchFacilities = async () => {
        try {
            setLoading(true);
            const response = await facilityService.getAllFacilities(page, rowsPerPage);
            setFacilities(response.content);
            setTotalElements(response.totalElements);
        } catch (error) {
            showSnackbar('Failed to fetch facilities', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const response = await clientService.getAllClients(0, 100);
            setClients(response.content);
        } catch (error) {
            showSnackbar('Failed to fetch clients', 'error');
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name || formData.name.trim().length < 2) {
            errors.name = 'Name must be at least 2 characters';
        }

        if (!formData.code || formData.code.trim().length < 2) {
            errors.code = 'Code is required and must be at least 2 characters';
        } else if (!/^[a-zA-Z0-9]+$/.test(formData.code)) {
            errors.code = 'Code must be alphanumeric';
        }

        if (!formData.clientId) {
            errors.clientId = 'Client is required';
        }

        if (!formData.admissionType) {
            errors.admissionType = 'Care type is required';
        }

        if (!formData.phone) {
            errors.phone = 'Phone is required';
        } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
            errors.phone = 'Invalid phone format';
        }

        if (!formData.address) {
            errors.address = 'Address is required';
        }

        if (!formData.city) {
            errors.city = 'City is required';
        }

        if (!formData.state) {
            errors.state = 'State is required';
        }

        if (!formData.country) {
            errors.country = 'Country is required';
        }

        if (!formData.postalCode) {
            errors.postalCode = 'Postal code is required';
        } else if (!/^[a-zA-Z0-9]{1,10}$/.test(formData.postalCode)) {
            errors.postalCode = 'Postal code must be alphanumeric and up to 10 characters';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
            errors.website = 'Website must be a valid URL';
        }

        if (formData.capacity && formData.capacity < 1) {
            errors.capacity = 'Capacity must be a positive number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleOpenDialog = (mode: 'create' | 'edit', facility?: FacilityDTO) => {
        setDialogMode(mode);
        if (mode === 'edit' && facility) {
            setSelectedFacility(facility);
            setFormData({
                name: facility.name,
                code: facility.code,
                clientId: facility.clientId,
                admissionType: facility.admissionType,
                email: facility.email || '',
                phone: facility.phone,
                fax: facility.fax || '',
                address: facility.address,
                city: facility.city,
                state: facility.state,
                country: facility.country,
                postalCode: facility.postalCode,
                subdomain: facility.subdomain || '',
                licenseNumber: facility.licenseNumber || '',
                npiNumber: facility.npiNumber || '',
                medicareProviderNumber: facility.medicareProviderNumber || '',
                medicaidProviderNumber: facility.medicaidProviderNumber || '',
                capacity: facility.capacity,
                timezone: facility.timezone || 'America/New_York',
                logoUrl: facility.logoUrl || '',
                website: facility.website || '',
                notes: facility.notes || '',
            });
        } else {
            setSelectedFacility(null);
            setFormData({
                name: '',
                code: '',
                clientId: '',
                admissionType: CareType.SNF,
                email: '',
                phone: '',
                fax: '',
                address: '',
                city: '',
                state: '',
                country: 'USA',
                postalCode: '',
                subdomain: '',
                licenseNumber: '',
                npiNumber: '',
                medicareProviderNumber: '',
                medicaidProviderNumber: '',
                capacity: undefined,
                timezone: 'America/New_York',
                logoUrl: '',
                website: '',
                notes: '',
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedFacility(null);
        setFormErrors({});
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            if (dialogMode === 'create') {
                await facilityService.createFacility(formData);
                showSnackbar('Facility created successfully', 'success');
            } else if (selectedFacility) {
                await facilityService.updateFacility(selectedFacility.id!, formData);
                showSnackbar('Facility updated successfully', 'success');
            }
            handleCloseDialog();
            fetchFacilities();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (facility: FacilityDTO) => {
        setFacilityToDelete(facility);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!facilityToDelete) return;

        try {
            setLoading(true);
            await facilityService.deleteFacility(facilityToDelete.id!);
            showSnackbar('Facility deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setFacilityToDelete(null);
            fetchFacilities();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Failed to delete facility', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    Facility
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog('create')}
                    sx={{
                        background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #238F8D 0%, #0F3A48 100%)',
                        },
                    }}
                >
                    Add Facility
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Care Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>City, State</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : facilities.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No facilities found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    facilities.map((facility) => (
                                        <TableRow key={facility.id} hover>
                                            <TableCell>{facility.name}</TableCell>
                                            <TableCell>{facility.code}</TableCell>
                                            <TableCell>{facility.clientName || '-'}</TableCell>
                                            <TableCell>
                                                <Chip label={facility.admissionType} size="small" color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell>{facility.phone}</TableCell>
                                            <TableCell>{`${facility.city}, ${facility.state}`}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={facility.isActive ? 'Active' : 'Inactive'}
                                                    color={facility.isActive ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenDialog('edit', facility)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(facility)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' ? 'Create New Facility' : 'Edit Facility'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Name *"
                            value={formData.name}
                            onChange={(e) => {
                                const value = e.target.value;


                                if (/^[A-Za-z\s]*$/.test(value)) {
                                    setFormData({ ...formData, name: value });
                                }
                            }}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            fullWidth
                        />

                        <TextField
                            label="Code *"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                            error={!!formErrors.code}
                            helperText={formErrors.code}
                            fullWidth
                        />
                        <FormControl fullWidth error={!!formErrors.clientId}>
                            <InputLabel>Client *</InputLabel>
                            <Select
                                value={formData.clientId}
                                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                                label="Client *"
                            >
                                {clients.map((client) => (
                                    <MenuItem key={client.id} value={client.id}>
                                        {client.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.clientId && <FormHelperText>{formErrors.clientId}</FormHelperText>}
                        </FormControl>
                        <FormControl fullWidth error={!!formErrors.admissionType}>
                            <InputLabel>Care Type *</InputLabel>
                            <Select
                                value={formData.admissionType}
                                onChange={(e) => setFormData({ ...formData, admissionType: e.target.value as CareType })}
                                label="Care Type *"
                            >
                                <MenuItem value={CareType.SNF}>SNF (Skilled Nursing Facility)</MenuItem>
                                <MenuItem value={CareType.HOSPICE}>Hospice</MenuItem>
                                <MenuItem value={CareType.BOTH}>Both</MenuItem>
                            </Select>
                            {formErrors.admissionType && <FormHelperText>{formErrors.admissionType}</FormHelperText>}
                        </FormControl>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                label="Phone *"
                                value={formData.phone}
                                onChange={(e) => {
                                    let value = e.target.value;


                                    if (/^\d*$/.test(value)) {

                                        if (value.length <= 10) {
                                            setFormData({ ...formData, phone: value });
                                        }
                                    }
                                }}
                                error={!!formErrors.phone}
                                helperText={formErrors.phone}
                                fullWidth
                            />

                            <TextField
                                label="Fax"
                                value={formData.fax}
                                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            fullWidth
                        />
                        <TextField
                            label="Address *"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            error={!!formErrors.address}
                            helperText={formErrors.address}
                            fullWidth
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="City *"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                error={!!formErrors.city}
                                helperText={formErrors.city}
                                fullWidth
                            />
                            <TextField
                                label="State *"
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                error={!!formErrors.state}
                                helperText={formErrors.state}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Country *"
                                value={formData.country}
                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                error={!!formErrors.country}
                                helperText={formErrors.country}
                                fullWidth
                            />
                            <TextField
                                label="Postal Code *"
                                value={formData.postalCode}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10);
                                    setFormData({ ...formData, postalCode: val });
                                }}
                                error={!!formErrors.postalCode}
                                helperText={formErrors.postalCode}
                                inputProps={{ maxLength: 10 }}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            label="Capacity"
                            type="number"
                            value={formData.capacity || ''}
                            onChange={(e) => setFormData({ ...formData, capacity: e.target.value ? parseInt(e.target.value) : undefined })}
                            error={!!formErrors.capacity}
                            helperText={formErrors.capacity}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel shrink>Time Zone</InputLabel>
                            <Select
                                displayEmpty
                                value={formData.timezone ?? ''}
                                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                                label="Time Zone"
                            >
                                <MenuItem value="America/New_York">🌎 America/New_York — Eastern Time</MenuItem>
                                <MenuItem value="America/Chicago">🌎 America/Chicago — Central Time</MenuItem>
                                <MenuItem value="America/Denver">🌎 America/Denver — Mountain Time</MenuItem>
                                <MenuItem value="America/Los_Angeles">🌎 America/Los_Angeles — Pacific Time</MenuItem>
                                <MenuItem value="America/Anchorage">🌎 America/Anchorage — Alaska Time</MenuItem>
                                <MenuItem value="Pacific/Honolulu">🌎 Pacific/Honolulu — Hawaii Time</MenuItem>
                                <MenuItem value="Europe/London">🌍 Europe/London — GMT</MenuItem>
                                <MenuItem value="Europe/Paris">🌍 Europe/Paris — Central European Time</MenuItem>
                                <MenuItem value="Europe/Berlin">🌍 Europe/Berlin — Central European Time</MenuItem>
                                <MenuItem value="Asia/Kolkata">🌏 Asia/Kolkata — India Standard Time</MenuItem>
                                <MenuItem value="Asia/Dubai">🌏 Asia/Dubai — Gulf Standard Time</MenuItem>
                                <MenuItem value="Asia/Singapore">🌏 Asia/Singapore — Singapore Time</MenuItem>
                                <MenuItem value="Asia/Tokyo">🌏 Asia/Tokyo — Japan Standard Time</MenuItem>
                                <MenuItem value="Australia/Sydney">🌏 Australia/Sydney — Australian Eastern Time</MenuItem>
                                <MenuItem value="Pacific/Auckland">🌏 Pacific/Auckland — New Zealand Time</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Sub Domain"
                            value={formData.subdomain}
                            onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="License Number"
                            value={formData.licenseNumber}
                            onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="NPI Number"
                            value={formData.npiNumber}
                            onChange={(e) => setFormData({ ...formData, npiNumber: e.target.value })}
                            fullWidth
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Medicare Provider Number"
                                value={formData.medicareProviderNumber}
                                onChange={(e) => setFormData({ ...formData, medicareProviderNumber: e.target.value })}
                                fullWidth
                            />
                            <TextField
                                label="Medicaid Provider Number"
                                value={formData.medicaidProviderNumber}
                                onChange={(e) => setFormData({ ...formData, medicaidProviderNumber: e.target.value })}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            label="Website"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            error={!!formErrors.website}
                            helperText={formErrors.website}
                            fullWidth
                        />
                        <TextField
                            label="Notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : dialogMode === 'create' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete facility "{facilityToDelete?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FacilitySettingsPage;